import type {SketchSolverIslandSnapshot, SketchSolverRecord} from '../../../../history/solver';
import {
    areSketchSolverIslandSnapshotsEqual,
    createSketchSolverRecord,
} from '../../../../history/solver';
import {
    createSketchSolverRuntimeSnapshot,
    createUnionSketchSolverIslandId,
    withSketchSolverRuntimeSnapshotIslandId,
} from './SketchSolverSnapshotFactory';
import {restoreSketchSolverRuntimeSnapshot} from './SketchSolverRuntimeRestorer';
import type {
    BeginSketchSolverHistorySessionInput,
    CommitSketchSolverHistorySessionInput,
    SketchSolverHistoryRuntimePort,
    SketchSolverHistoryRuntimeSession,
} from './SketchSolverRuntimeTypes';

/**
 * solver history 运行时端口挂载键。
 */
const SKETCH_SOLVER_HISTORY_RUNTIME_PORT = Symbol('SketchSolverHistoryRuntimePort');

/**
 * 可挂载 solver history 运行时端口的对象。
 */
interface SketchSolverHistoryRuntimePortHost {
    /** 当前对象树绑定的 solver history 运行时端口。 */
    [SKETCH_SOLVER_HISTORY_RUNTIME_PORT]?: SketchSolverHistoryRuntimePort;
    /** Three.js 父级。 */
    readonly parent?: unknown;
}

/**
 * 默认 record 序列。
 */
let defaultRecordSequence = 0;

/**
 * 创建默认 solver record ID。
 *
 * @param source 记录来源。
 * @returns record ID。
 */
function createDefaultRecordId(source: string): string {
    defaultRecordSequence += 1;
    return `solver-record:${source}:${defaultRecordSequence}`;
}

/**
 * 配置草图 solver 历史运行时端口。
 *
 * @param port 运行时端口。
 * @param hostObject Viewer 场景或文档宿主对象。
 * @returns 取消配置函数。
 * @throws 当未传入宿主对象时抛出错误。
 */
export function configureSketchSolverHistoryRuntime(
    port: SketchSolverHistoryRuntimePort,
    hostObject?: object | null,
): () => void {
    if (!hostObject) {
        throw new Error('配置草图 solver history runtime 时必须传入 Viewer 场景或文档宿主对象。');
    }

    Reflect.set(hostObject, SKETCH_SOLVER_HISTORY_RUNTIME_PORT, port);

    return () => {
        if (Reflect.get(hostObject, SKETCH_SOLVER_HISTORY_RUNTIME_PORT) === port) {
            Reflect.deleteProperty(hostObject, SKETCH_SOLVER_HISTORY_RUNTIME_PORT);
        }
    };
}

/**
 * 清空草图 solver 历史运行时端口。
 *
 * @deprecated 该入口只保留给旧测试；生产代码应使用 configure 返回的 dispose。
 */
export function clearSketchSolverHistoryRuntime(): void {
    // 运行时端口已绑定到 Viewer 场景树，不再维护模块级全局状态。
}

/**
 * 从种子对象父级链解析草图 solver 历史运行时端口。
 *
 * @param seedObjects solver island 种子对象。
 * @returns 当前对象树绑定的端口。
 */
export function getSketchSolverHistoryRuntime(
    seedObjects: readonly unknown[] = [],
): SketchSolverHistoryRuntimePort | null {
    for (const seedObject of seedObjects) {
        let current: unknown = seedObject;
        while (current && typeof current === 'object') {
            const port = Reflect.get(current, SKETCH_SOLVER_HISTORY_RUNTIME_PORT);
            if (port) {
                return port as SketchSolverHistoryRuntimePort;
            }
            current = (current as SketchSolverHistoryRuntimePortHost).parent ?? null;
        }
    }

    return null;
}

/**
 * 判断 snapshot 是否有效。
 *
 * @param snapshot snapshot。
 * @returns 是否有效。
 */
function hasSnapshot(snapshot: SketchSolverIslandSnapshot | null): snapshot is SketchSolverIslandSnapshot {
    return snapshot !== null;
}

/**
 * 创建归一化 before/after record。
 *
 * @param input 会话输入。
 * @param port 当前端口。
 * @param before before snapshot。
 * @param after after snapshot。
 * @returns solver record；无语义变化时返回 null。
 */
function createNormalizedSolverRecord(
    input: BeginSketchSolverHistorySessionInput,
    port: SketchSolverHistoryRuntimePort,
    before: SketchSolverIslandSnapshot,
    after: SketchSolverIslandSnapshot,
): SketchSolverRecord | null {
    const islandId = createUnionSketchSolverIslandId(before, after);
    const normalizedBefore = withSketchSolverRuntimeSnapshotIslandId(before, islandId);
    const normalizedAfter = withSketchSolverRuntimeSnapshotIslandId(after, islandId);

    if (areSketchSolverIslandSnapshotsEqual(normalizedBefore, normalizedAfter)) {
        return null;
    }

    return createSketchSolverRecord({
        recordId: port.createRecordId?.(input.source) ?? createDefaultRecordId(input.source),
        source: input.source,
        before: normalizedBefore,
        after: normalizedAfter,
        dragCommit: input.dragCommit,
        automaticRelation: input.automaticRelation,
        automaticDrivenDimension: input.automaticDrivenDimension,
    });
}

/**
 * 开启草图 solver 历史会话。
 *
 * 设计说明：
 * 1. pointermove 不调用该函数，拖拽只在 start 采 before，mouseup/stop 采 after；
 * 2. Cancel/Esc/失败操作调用 discard，不生成全局模型历史；
 * 3. before/after 使用 union scope 归一化 islandId，保证新增或删除约束时仍是同一事务影响域。
 *
 * @param input 会话输入。
 * @returns 历史会话；未配置端口或没有有效草图对象时返回 null。
 */
export function beginSketchSolverHistorySession(
    input: BeginSketchSolverHistorySessionInput,
): SketchSolverHistoryRuntimeSession | null {
    const port = getSketchSolverHistoryRuntime(input.seedObjects);
    if (!port) {
        return null;
    }

    const beforeResult = createSketchSolverRuntimeSnapshot({
        seedObjects: input.seedObjects,
        supplementProvider: port.supplementProvider,
    });

    if (!hasSnapshot(beforeResult.snapshot)) {
        return null;
    }

    let closed = false;

    return {
        async commit(commitInput?: CommitSketchSolverHistorySessionInput) {
            if (closed) {
                return null;
            }

            closed = true;
            const afterSeedObjects = commitInput?.seedObjects ?? input.seedObjects;
            let record: SketchSolverRecord | null = null;

            try {
                const afterResult = createSketchSolverRuntimeSnapshot({
                    seedObjects: afterSeedObjects,
                    supplementProvider: port.supplementProvider,
                });

                if (!hasSnapshot(afterResult.snapshot)) {
                    throw new Error(`solver history ${input.source} 提交失败：after snapshot 为空。`);
                }

                record = createNormalizedSolverRecord(
                    input,
                    port,
                    beforeResult.snapshot,
                    afterResult.snapshot,
                );

                if (!record) {
                    return null;
                }

                await port.onSolverRecord(record, {
                    source: input.source,
                    dragCommit: input.dragCommit,
                    automaticRelation: input.automaticRelation,
                    automaticDrivenDimension: input.automaticDrivenDimension,
                });
            } catch (error) {
                // 历史提交失败时必须尽量恢复到 before，避免模型已经变更但没有 undo 记录。
                restoreSketchSolverRuntimeSnapshot({
                    seedObjects: afterSeedObjects,
                    snapshot: beforeResult.snapshot,
                });
                throw error;
            }

            return record;
        },
        discard() {
            closed = true;
        },
        rollback(rollbackInput?: CommitSketchSolverHistorySessionInput) {
            closed = true;
            restoreSketchSolverRuntimeSnapshot({
                seedObjects: rollbackInput?.seedObjects ?? input.seedObjects,
                snapshot: beforeResult.snapshot,
            });
        },
    };
}
